import { createContextApi, invalidatesOne, providesOne, providesList, createCursorPagedEndpoint } from '@quickbee/store';

// src/index.ts
var IDENTITY_TAGS = ["Me", "Session", "Kyc", "User"];
var identityEndpoints = (build) => ({
  // ─── /me ────────────────────────────────────────────────────────────────────
  getMe: build.query({
    query: () => ({ url: "/me" }),
    providesTags: [{ type: "Me", id: "SELF" }]
  }),
  updateMe: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/me",
      method: "PUT",
      body,
      // `PUT /me` is @IdempotentRequest. A key derived from the body would be
      // wrong in the other direction: renaming A → B → A would replay the second
      // response and silently skip the third write.
      idempotencyKey: `me-update:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "Me", id: "SELF" }]
  }),
  listMySessions: build.query({
    query: () => ({ url: "/me/sessions" }),
    providesTags: (result) => providesList("Session", result)
  }),
  revokeSession: build.mutation({
    query: (sessionId) => ({
      url: `/me/sessions/${sessionId}`,
      method: "DELETE",
      // Revoking the same session twice is the same intent, so the session id is
      // itself the stable key.
      idempotencyKey: `session-revoke:${sessionId}`
    }),
    invalidatesTags: (_r, _e, sessionId) => invalidatesOne("Session", sessionId)
  }),
  /**
   * Revokes every session **including the caller's own** — the backend does not
   * exempt it. Treat a success as a logout and let `@quickbee/auth` tear the
   * session down rather than leaving the UI on a screen whose next call 401s.
   */
  revokeAllSessions: build.mutation({
    query: ({ clientRequestId }) => ({
      url: "/me/sessions/revoke-all",
      method: "POST",
      idempotencyKey: `session-revoke-all:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "Session", id: "LIST" }]
  }),
  // ─── /kyc ───────────────────────────────────────────────────────────────────
  /**
   * ⚠️ `404 NOT_FOUND` is the normal answer for an account that has never
   * submitted KYC — `GetKycStatusQueryHandler` throws `NotFoundException("KycProfile")`
   * rather than returning a `NOT_STARTED` row. A KYC banner must read the 404 as
   * "not started", not as an error toast.
   */
  getMyKycStatus: build.query({
    query: () => ({ url: "/kyc/status" }),
    providesTags: [{ type: "Kyc", id: "SELF" }]
  }),
  /** Same 404-means-not-started caveat as {@link getMyKycStatus}. Requires `kyc:read:tenant`. */
  getUserKycStatus: build.query({
    query: (userId) => ({ url: `/kyc/${userId}/status` }),
    providesTags: (_r, _e, userId) => providesOne("Kyc", userId)
  }),
  /**
   * The KYC review queue (B1) — who is waiting on {@link decideKyc}, oldest first.
   * Requires `kyc:read:tenant`.
   *
   * Tagged by `userId` rather than `id`, because that is the row's identity here;
   * {@link decideKyc}'s `invalidatesOne('Kyc', userId)` therefore drops both the
   * decided row and the list, and a decided submission leaves the queue without a
   * manual refetch.
   */
  listKycSubmissions: build.query({
    query: (args) => ({ url: "/kyc/submissions", params: args ?? {} }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Kyc", result, "userId"),
    keepUnusedDataFor: 30
  }),
  /**
   * What the reviewer on {@link decideKyc} actually reviews (B2). Requires
   * `kyc:read:tenant` — never `:own`.
   *
   * **Not cached beyond the screen.** `imageUrl` is a presigned link with a TTL in
   * minutes, so a cache entry outlives the URL inside it and would render as a
   * broken image rather than a refetch. Reading this is also an audited, sensitive
   * access on the backend — re-reading on mount is the honest behaviour, not a
   * cost to optimise away.
   *
   * `404` means the account has no KYC profile at all, which is distinct from
   * `200 []` ("profile exists, no documents"); treat it as the state it is.
   */
  getKycDocuments: build.query({
    query: (userId) => ({ url: `/kyc/${userId}/documents` }),
    providesTags: (_r, _e, userId) => providesOne("Kyc", userId),
    keepUnusedDataFor: 0
  }),
  submitKyc: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/kyc/submissions",
      method: "POST",
      body,
      // I9: stable across user retries. Two taps on "Submit" must not create two
      // submissions competing for one decision.
      idempotencyKey: `kyc-submit:${clientRequestId}`
    }),
    invalidatesTags: [
      { type: "Kyc", id: "SELF" },
      { type: "Me", id: "SELF" }
    ]
  }),
  decideKyc: build.mutation({
    query: ({ userId, clientRequestId, ...body }) => ({
      url: `/kyc/${userId}/decision`,
      method: "POST",
      body,
      // The controller reads `X-Idempotency-Key` itself and carries it into
      // `RecordKycDecisionCommand`, so this value becomes the decision's dedupe
      // identity in the domain — not merely a filter-level replay guard. One id
      // per reviewer action; reusing it for a later re-review would be ignored.
      idempotencyKey: `kyc-decision:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => [
      ...invalidatesOne("Kyc", userId),
      ...invalidatesOne("User", userId)
    ]
  }),
  // ─── /users ─────────────────────────────────────────────────────────────────
  /**
   * ⚠️ **First page only.** The response is a `PagedApiResponse`, but
   * `SearchUsersQueryHandler` pins the query to Spring page 0 and the controller
   * builds its envelope with `Page.ofOffset(…, offset = 0)` — so `pagination.cursor`
   * is always `null` and there is no way to ask for page 2. `pagination.total` is
   * synthesised too (`size + 1` whenever the page comes back full), so it must not
   * be rendered as a result count; use it only as "there are more than these".
   *
   * Raise `size` (max 100) to widen the window, and filter rather than paginate.
   * Real pagination needs a backend change (a count query plus a cursor-aware
   * repository), tracked against identity-service.
   */
  listUsers: build.query({
    query: (args) => ({ url: "/users", params: args ?? {} }),
    providesTags: (result) => providesList("User", result)
  }),
  getUser: build.query({
    query: (id) => ({ url: `/users/${id}` }),
    providesTags: (_r, _e, id) => providesOne("User", id)
  }),
  provisionPartnerUser: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/users/partner",
      method: "POST",
      body,
      idempotencyKey: `user-partner:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "User", id: "LIST" }]
  }),
  provisionPlatformUser: build.mutation({
    query: ({ clientRequestId, ...body }) => ({
      url: "/users/platform",
      method: "POST",
      body,
      idempotencyKey: `user-platform:${clientRequestId}`
    }),
    invalidatesTags: [{ type: "User", id: "LIST" }]
  }),
  /**
   * A role assignment is **reversible** — assign → revoke → re-assign is a
   * legitimate sequence, and `AssignRoleCommandHandler` executes each one. A key
   * built from `(userId, role, scope, orgScopeId)` is stable across those three
   * calls, so inside the filter's 24 h window the third would replay the first
   * one's cached profile and never reach the handler. The caller mints one id
   * per committed assignment instead.
   */
  assignRole: build.mutation({
    query: ({ userId, clientRequestId, ...body }) => ({
      url: `/users/${userId}/roles`,
      method: "POST",
      body,
      idempotencyKey: `role-assign:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /** Reversible in the same way as {@link assignRole}; same keying rule. */
  revokeRole: build.mutation({
    query: ({ userId, role, clientRequestId }) => ({
      url: `/users/${userId}/roles/${role}`,
      method: "DELETE",
      idempotencyKey: `role-revoke:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /**
   * `ACTIVE → SUSPENDED`, and its inverse is {@link reactivateUser} — the pair
   * cycles. `user-suspend:{id}` would make the second suspension of the same
   * account within 24 h a silent no-op that returns the first one's body, so the
   * console would show `SUSPENDED` for an account the backend left `ACTIVE`.
   */
  suspendUser: build.mutation({
    query: ({ userId, clientRequestId, reason }) => ({
      url: `/users/${userId}/suspend`,
      method: "POST",
      // `@RequestBody(required = false)`, and `reason` is optional — send an
      // empty object rather than `{ reason: undefined }` so nothing depends on
      // how the transport happens to serialise `undefined`.
      body: reason === void 0 ? {} : { reason },
      idempotencyKey: `user-suspend:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /** The other half of the cycle; same keying rule as {@link suspendUser}. */
  reactivateUser: build.mutation({
    query: ({ userId, clientRequestId }) => ({
      url: `/users/${userId}/reactivate`,
      method: "POST",
      idempotencyKey: `user-reactivate:${userId}:${clientRequestId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  }),
  /**
   * Initiates DPDP erasure. Returns `204` — the account is *not* re-read, so the
   * caller must refetch rather than expect a profile back. A stable key means a
   * double submit erases once.
   */
  eraseUser: build.mutation({
    query: ({ userId, reason }) => ({
      url: `/users/${userId}/erasure`,
      method: "POST",
      body: reason === void 0 ? {} : { reason },
      idempotencyKey: `user-erasure:${userId}`
    }),
    invalidatesTags: (_r, _e, { userId }) => invalidatesOne("User", userId)
  })
  // ─── Deliberately absent ────────────────────────────────────────────────────
  // `GET /identity/customers/{id}/display-info` is not defined here. It is
  // `@PubliclyAccessible` in the sense of "no JWT", is reachable only inside the
  // VPC, and returns the customer's **full E.164 phone** for S2S matching. It has
  // no gateway route by design, so a browser call 404s at the edge — and if one
  // ever did reach it, the SDK would be handing unmasked PII to a client bundle.
  // Operator screens that need a name for a customer id must go through a
  // context that exposes a masked projection.
});

// src/index.ts
var identityApi = createContextApi({
  context: "identity",
  reducerPath: "identityApi",
  tagTypes: IDENTITY_TAGS,
  mocks: () => import('./mocks-7D3H6ZL5.js').then((module) => module.identityMocks),
  endpoints: identityEndpoints
});
var {
  useGetMeQuery,
  useUpdateMeMutation,
  useListMySessionsQuery,
  useRevokeSessionMutation,
  useRevokeAllSessionsMutation,
  useGetMyKycStatusQuery,
  useGetUserKycStatusQuery,
  useListKycSubmissionsQuery,
  useGetKycDocumentsQuery,
  useSubmitKycMutation,
  useDecideKycMutation,
  useListUsersQuery,
  useGetUserQuery,
  useProvisionPartnerUserMutation,
  useProvisionPlatformUserMutation,
  useAssignRoleMutation,
  useRevokeRoleMutation,
  useSuspendUserMutation,
  useReactivateUserMutation,
  useEraseUserMutation
} = identityApi;

export { IDENTITY_TAGS, identityApi, useAssignRoleMutation, useDecideKycMutation, useEraseUserMutation, useGetKycDocumentsQuery, useGetMeQuery, useGetMyKycStatusQuery, useGetUserKycStatusQuery, useGetUserQuery, useListKycSubmissionsQuery, useListMySessionsQuery, useListUsersQuery, useProvisionPartnerUserMutation, useProvisionPlatformUserMutation, useReactivateUserMutation, useRevokeAllSessionsMutation, useRevokeRoleMutation, useRevokeSessionMutation, useSubmitKycMutation, useSuspendUserMutation, useUpdateMeMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map